import axios from 'axios';
import env from 'src/env.json';
import { MediaObject as BaseMediaObject } from 'src/api/services/MediaClient/MediaClient.types';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import * as tus from 'tus-js-client';
// Import the shared AI Agent API service
import { aiAgentApiService } from 'src/services/api/ai-agent-api.service';

// Define FormMediaObject interface with all necessary properties for compatibility
export interface FormMediaObject {
  id?: string;
  // Original properties
  name: string;
  type: string;
  size?: number;
  preview?: string;
  url?: string;
  // Additional properties used in the component
  file_name?: string;
  mime_type?: string;
  index_url?: string;
  // Properties for file upload handling
  file?: File;
  upload_id?: string;
  // Metadata properties
  tags?: string[];
  description?: string;
  author?: string;
  location?: string;
  copyright?: string;
}

// Define extended MediaObject interface with directory support and metadata
export interface MediaObject extends Omit<BaseMediaObject, 'created_at' | 'updated_at'> {
  directory_id?: string | null;
  tags?: string[];
  created_at: string;
  updated_at: string;
  metadata?: {
    description?: string;
    copyright?: string;
    author?: string;
    location?: string;
    customFields?: Record<string, string>;
  };
}

// Define the response structure from the API
export interface MediaResponse {
  files: MediaObject[];
  folders: string[];
  currentPath: string[];
}

// Define the base URLs for different APIs
const BE_API_URL = env.API_URL || 'http://localhost/api';

/**
 * RequestMediaService - A specialized version of MediaService for the request form
 * This service handles media operations for the request form with completely isolated state
 * from the main media library to prevent any state sharing or interference
 */
export class RequestMediaService {
  private agentId: string | null = null;
  private readonly _beApiUrl: string;
  // Use a completely separate cache that's not shared with any other media service
  private static requestFormCache = new Map<string, { data: any; timestamp: number }>();
  private static CACHE_TTL = 60000; // 1 minute cache validity
  // Reference to the shared AI Agent API service
  private aiAgentApiService = aiAgentApiService;

  // Global registry to track all active uploads
  public static activeUploads = new Map<string, tus.Upload>();

  // Flag to track if this service is in request form context
  private isInRequestFlow = true;

  constructor(beApiUrl: string = BE_API_URL) {
    this._beApiUrl = beApiUrl;
  }

  /**
   * Get the base URL for API calls
   */
  get beApiUrl() {
    return this._beApiUrl;
  }

  /**
   * Helper method to get auth headers for API calls
   */
  get headers(): Record<string, string> {
    // Get the token using the proper AuthUtils method
    const jwt = AuthUtils.getJwt();

    const headers: Record<string, string> = {
      Authorization: `Bearer ${jwt.token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Add x-brand-id header if a brand is selected
    const selectedBrandId = this.getSelectedBrandId();
    if (selectedBrandId) {
      headers['x-brand-id'] = selectedBrandId;
    }

    return headers;
  }

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Gets the agent ID, fetching it first if not already available
   * Uses the shared AI Agent API service directly
   */
  async getAgentId(): Promise<string> {
    // If we already have an agent ID cached, return it
    if (this.agentId) {
      return this.agentId;
    }

    try {
      // Use the shared service to get the agent ID
      const agentId = await aiAgentApiService.getAgentId();

      // Store it locally for future use
      this.agentId = agentId;

      // Cache the agent ID for future use
      if (this.agentId) {
        localStorage.setItem('be_creatives_agent_id', this.agentId);
      }

      return this.agentId;
    } catch (error) {
      console.error('RequestMediaService: Error getting agent ID:', error);

      // Development fallback - only use it if the API call fails
      if (process.env.NODE_ENV === 'development') {
        this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
        console.warn(
          'RequestMediaService: Using development fallback agent ID in getAgentId:',
          this.agentId,
        );
        return this.agentId;
      }

      return '';
    }
  }

  /**
   * Fetches the agent ID from the backend
   * This should be called before any API requests that require the agent_id
   * Uses the shared AI Agent API service instead of making a direct API call
   */
  async initializeAgentId(): Promise<void> {
    // First, clear any problematic cached agent ID
    if (localStorage.getItem('be_creatives_agent_id') === 'default-agent-id-for-testing') {
      console.warn('Removing test agent ID from localStorage');
      localStorage.removeItem('be_creatives_agent_id');
    }

    try {
      // Use the shared service to get the agent ID
      const agentId = await aiAgentApiService.getAgentId();

      // Store the agent ID locally
      this.agentId = agentId;

      // Cache the agent ID for future use
      if (this.agentId) {
        localStorage.setItem('be_creatives_agent_id', this.agentId);
      }
    } catch (error: any) {
      console.error(
        'RequestMediaService: Error retrieving agent ID from shared service:',
        error.message,
      );
      if (error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
        });
      }

      // Development fallback - only use it if the API call fails
      if (process.env.NODE_ENV === 'development') {
        this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
        console.warn(
          'RequestMediaService: Using development fallback agent ID after fetch error:',
          this.agentId,
        );
        return; // Return early since we have a fallback ID
      }

      // In production, set agentId to null and propagate the error
      this.agentId = null;
      throw error;
    }
  }

  /**
   * Adapts API file objects to the MediaObject format expected by the application
   * @param apiFiles Array of file objects from the API
   * @returns Array of MediaObject compatible with the application
   */
  private adaptApiFilesToMediaObjects(apiFiles: any[]): MediaObject[] {
    if (!Array.isArray(apiFiles)) {
      console.error('Expected array of files, got:', typeof apiFiles);
      return [];
    }

    return apiFiles
      .map((file) => {
        // Ensure we have a valid object
        if (!file || typeof file !== 'object') {
          console.error('Invalid file object:', file);
          return null;
        }

        // Create a MediaObject from the API response using proper field mapping
        // Map API response fields to the expected MediaObject structure
        const mediaObject: MediaObject = {
          id: file.id || '',
          file_name: file.file_name || file.name || 'Unnamed file',
          mime_type: file.mime_type || file.file_type || 'application/octet-stream',
          size: file.size || file.file_size || 0,
          collection_name: file.collection_name || 'default',
          index_url: file.index_url || '',
          url: file.url || '',
          tags: Array.isArray(file.tags) ? file.tags : [],
          directory_id: file.directory_id || null,
          created_at: file.created_at || new Date().toISOString(),
          updated_at: file.updated_at || new Date().toISOString(),
          // Required properties from MediaObject interface
          representations: file.representations || [],
          generated_resolutions: file.generated_resolutions || {},
          // Include generated_conversions for thumbnails if available
          generated_conversions: file.generated_conversions || {},
          // Add custom_properties field which might be needed
          custom_properties: file.custom_properties || {},
          // Add a comment field if available
          comment: file.comment,
          metadata: {
            description: file.description || file.metadata?.description || '',
            copyright: file.copyright || file.metadata?.copyright || '',
            author: file.author || file.metadata?.author || '',
            location: file.location || file.metadata?.location || '',
            customFields: file.metadata?.customFields || {},
          },
        };

        return mediaObject;
      })
      .filter(Boolean) as MediaObject[];
  }

  /**
   * Normalizes a directory path to prevent duplications
   * @param path The path to normalize
   * @returns The normalized path without duplicate segments
   */
  private normalizePath(path: string | null | undefined): string | undefined {
    if (!path) return undefined;

    // Split the path into segments and remove duplicates
    const segments = path.split('/').filter(Boolean);
    const uniqueSegments: string[] = [];
    const seenSegments = new Set<string>();

    // Filter out duplicated path segments
    for (const segment of segments) {
      // Only add segments that aren't already seen
      if (!seenSegments.has(segment)) {
        uniqueSegments.push(segment);
        seenSegments.add(segment);
      }
    }

    return uniqueSegments.length > 0 ? uniqueSegments.join('/') : undefined;
  }

  /**
   * Get media items from the specified directory
   *
   * @param directoryId Optional directory ID or name
   * @param forceRefresh Whether to force a refresh from the API
   * @param fullPath Optional full directory path
   * @param mimeType Optional mime type filter
   * @param searchQuery Optional search query to filter media by name
   * @param brandId Optional brand ID to filter media by brand
   * @returns Promise with media items
   */
  async getMediaItems(
    directoryId?: string | null,
    forceRefresh = false,
    fullPath?: string,
    mimeType?: string,
    searchQuery?: string,
    brandId?: string,
  ): Promise<MediaResponse> {
    // Determine the directory path from either directoryId or fullPath
    let directoryPath: string | undefined;

    // Use fullPath if provided, otherwise use directoryId as the path
    if (fullPath) {
      directoryPath = fullPath;
    } else if (directoryId) {
      directoryPath = directoryId;
    }

    // Create a cache key based on all parameters including search query and brand ID
    const trimmedSearchQuery = searchQuery ? searchQuery.trim() : '';
    const hasSearchQuery = trimmedSearchQuery !== '';
    const cacheKey = `mediaItems:${directoryPath || ''}:${mimeType || 'all'}${hasSearchQuery ? `:search-${trimmedSearchQuery}` : ''}${brandId ? `:brand-${brandId}` : ''}`;
    const now = Date.now();

    // Check if we have a valid cached response
    if (!forceRefresh && RequestMediaService.requestFormCache.has(cacheKey)) {
      const cached = RequestMediaService.requestFormCache.get(cacheKey);
      if (cached && now - cached.timestamp < RequestMediaService.CACHE_TTL) {
        return cached.data;
      }
    }

    try {
      // Force initialization of the agent ID
      await this.initializeAgentId();

      // Get the agent ID (should be initialized now)
      const agent_id = await this.getAgentId();

      if (!agent_id) {
        console.error('Missing agent_id, cannot fetch media items');
        throw new Error('Missing agent_id, cannot fetch media items');
      }

      // Build query parameters
      // Make sure we're not using the test agent ID that doesn't work with the real API
      let agent_id_param = agent_id;
      if (agent_id === 'default-agent-id-for-testing') {
        console.warn('Converting test agent ID to standard default agent ID for API call');
        agent_id_param = 'default-agent-id';
      }
      const params: Record<string, any> = { agent_id: agent_id_param };

      // Add directory path if provided
      if (directoryPath && directoryPath.trim() !== '') {
        // CRITICAL FIX: Ensure the path is correctly formatted
        // Remove any leading slashes before sending to API
        const cleanPath = directoryPath.startsWith('/')
          ? directoryPath.substring(1)
          : directoryPath;

        // Ensure we don't have any double slashes in the path
        const pathWithoutDoubleSlashes = cleanPath.replace(/\/+/g, '/');

        // NEW FIX: Prevent path segment duplication using our normalization function
        const normalizedPath = this.normalizePath(pathWithoutDoubleSlashes) || '';

        // Log the normalized path to help with debugging
        console.debug(`📂 Normalized API path: ${normalizedPath} (original: ${directoryPath})`);

        params.path = normalizedPath;
      }

      // Add mime type filter if provided
      if (mimeType) {
        params.mime_type = mimeType;
      }

      // Add search query parameter if provided
      if (searchQuery && searchQuery.trim() !== '') {
        params.query = searchQuery.trim();
      }

      // Add brand ID parameter as user_id if provided (backend expects brand ID as user_id)
      if (brandId && brandId.trim() !== '') {
        params.user_id = brandId.trim();
      }

      // Use the shared AI Agent API service with forceRefresh parameter
      const responseData = await this.aiAgentApiService.getMediaItems(params, forceRefresh);

      // Process the response data
      let mediaResponse: MediaResponse;

      // Construct a currentPath entry if we have a directory path
      let currentPathEntries: any[] = [];
      if (directoryPath && directoryPath.trim() !== '') {
        currentPathEntries = [
          {
            id: directoryPath,
            name: directoryPath,
            path: `/${directoryPath}`,
          },
        ];
      }

      // Handle different response formats
      if (responseData && responseData.data) {
        // Handle wrapped response format
        const wrappedData = responseData.data;
        mediaResponse = {
          files: Array.isArray(wrappedData.files)
            ? this.adaptApiFilesToMediaObjects(wrappedData.files)
            : [],
          // CRITICAL: Keep folders as a simple string array - this is what MediaGallery expects
          folders: wrappedData.folders || [],
          // Use our constructed currentPath or fall back to response data
          currentPath:
            currentPathEntries.length > 0
              ? currentPathEntries
              : Array.isArray(wrappedData.currentPath)
                ? wrappedData.currentPath
                : [],
        };
      } else {
        // Handle direct response format
        mediaResponse = {
          files: Array.isArray(responseData.files)
            ? this.adaptApiFilesToMediaObjects(responseData.files)
            : [],
          // CRITICAL: Keep folders as a simple string array - this is what MediaGallery expects
          folders: responseData.folders || [],
          // Use our constructed currentPath or fall back to response data
          currentPath:
            currentPathEntries.length > 0
              ? currentPathEntries
              : Array.isArray(responseData.currentPath)
                ? responseData.currentPath
                : [],
        };
      }

      // Cache the result
      RequestMediaService.requestFormCache.set(cacheKey, {
        data: mediaResponse,
        timestamp: now,
      });

      return mediaResponse;
    } catch (error) {
      console.error('Error fetching media items:', error);
      // Return empty response if the API call fails
      return {
        files: [],
        folders: [],
        currentPath: [],
      };
    }
  }

  /**
   * Reset any cached directory path to ensure the request form has a clean state
   * @param clearAll If true, will clear all cache. If false, only clears directory-related entries
   * @param preserveCurrentPath If true, will preserve the current directory path (useful during navigation)
   */
  resetDirectoryPath(clearAll: boolean = false, preserveCurrentPath: boolean = false): void {
    // Set flag to indicate we're working with the request media context
    this.isInRequestFlow = true;

    if (clearAll) {
      // Clear the entire cache
      RequestMediaService.requestFormCache.clear();
      return;
    }

    // Clear the cache for any directory-related entries
    const keysToDelete: string[] = [];
    RequestMediaService.requestFormCache.forEach((_, key: string) => {
      const shouldDelete =
        key.includes('directory') ||
        key.includes('path') ||
        (key.includes('mediaItems') && (!preserveCurrentPath || !key.includes('current')));

      if (shouldDelete) {
        keysToDelete.push(key);
      }
    });

    // Delete identified keys
    keysToDelete.forEach((key) => {
      RequestMediaService.requestFormCache.delete(key);
    });
  }

  /**
   * Polls the analysis status until completion or timeout
   * Delegates to aiAgentApiService for shared implementation
   *
   * Note: Changed from unlimited polling (Infinity) to finite timeout (~3-4 min)
   * to prevent indefinite waiting on stuck analyses. Returns last known status
   * on timeout rather than throwing, allowing UI to show partial results.
   *
   * @param mediaId - ID of the media to poll
   * @param maxAttempts - Maximum number of polling attempts (default: 120, ~3-4 min with backoff)
   * @param initialInterval - Initial polling interval in milliseconds (default: 500ms)
   * @returns Promise with the final analysis result (or latest status if timeout)
   */
  async pollAnalysisStatus(
    mediaId: string,
    maxAttempts: number = 120,
    initialInterval: number = 500,
  ): Promise<any> {
    return this.aiAgentApiService.pollAnalysisStatus(mediaId, {
      maxAttempts,
      initialInterval,
    });
  }

  /**
   * Get a media item by ID
   * @param mediaId Media ID to retrieve
   * @param forceRefresh Whether to force refresh from API
   * @returns Promise with the media object
   */
  async getMediaItemById(mediaId: string, forceRefresh = false): Promise<MediaObject | null> {
    try {
      // Check if we have this in cache and it's still valid
      const cacheKey = `mediaItem:${mediaId}`;
      const cachedItem = RequestMediaService.requestFormCache.get(cacheKey);

      if (
        !forceRefresh &&
        cachedItem &&
        Date.now() - cachedItem.timestamp < RequestMediaService.CACHE_TTL
      ) {
        return cachedItem.data;
      }

      // Make sure we have the agent ID before making API calls
      await this.initializeAgentId();

      // Fetch the media item
      const response = await axios.get(`${this.beApiUrl}/ai-agent/media/${mediaId}`, {
        headers: this.headers,
        params: {
          agent_id: this.agentId,
        },
      });

      if (response.data) {
        // Process the media item to ensure consistent structure
        const mediaItem = this.adaptApiFilesToMediaObjects([response.data])[0];

        // Cache the result
        RequestMediaService.requestFormCache.set(cacheKey, {
          data: mediaItem,
          timestamp: Date.now(),
        });

        return mediaItem;
      }

      return null;
    } catch (error) {
      console.error(`Error fetching media item ${mediaId}:`, error);
      return null;
    }
  }

  /**
   * Get multiple media items by IDs in a single batch request
   * @param mediaIds Array of media IDs to retrieve
   * @param batchSize Number of IDs to include in each batch request (default: 20)
   * @returns Promise with an object mapping media IDs to their corresponding media objects
   */
  async batchGetMediaItems(
    mediaIds: string[],
    batchSize = 20,
  ): Promise<Record<string, MediaObject>> {
    try {
      // Ensure we have valid media IDs (not null, undefined, or empty)
      const validMediaIds = (mediaIds || []).filter((id) => !!id);

      if (validMediaIds.length === 0) {
        console.warn('No valid media IDs provided to batchGetMediaItems');
        return {};
      }

      // Process any media IDs that might be in the format "media://id" or similar
      const processedIds = validMediaIds.map((id) => {
        // If the ID contains '://', extract just the ID part after the protocol
        if (id.includes('://')) {
          const extractedId = id.split('://').pop() || '';
          return extractedId;
        }
        return id;
      });

      // First check cache for each ID to avoid unnecessary fetching
      const result: Record<string, MediaObject> = {};
      const uncachedIds: string[] = [];

      // Check which IDs we have in cache
      for (const mediaId of processedIds) {
        const cacheKey = `mediaItem:${mediaId}`;
        const cachedItem = RequestMediaService.requestFormCache.get(cacheKey);

        if (cachedItem && cachedItem.data) {
          // Use cached item if available
          result[mediaId] = cachedItem.data;

          // Also store under the original ID if it was different
          const originalIndex = processedIds.indexOf(mediaId);
          if (originalIndex !== -1) {
            const originalId = validMediaIds[originalIndex];
            if (originalId !== mediaId) {
              result[originalId] = cachedItem.data;
            }
          }
        } else {
          // Add to list of IDs to fetch
          uncachedIds.push(mediaId);
        }
      }

      // If all IDs were cached, return early
      if (uncachedIds.length === 0) {
        return result;
      }

      // Fetch uncached IDs individually using getMediaItemById

      for (const mediaId of uncachedIds) {
        try {
          // Use the individual getMediaItemById method which handles caching and error recovery
          const mediaItem = await this.getMediaItemById(mediaId, false);

          if (mediaItem) {
            // Store in the result
            result[mediaId] = mediaItem;

            // Also store under the original ID if it was different
            const originalIndex = processedIds.indexOf(mediaId);
            if (originalIndex !== -1) {
              const originalId = validMediaIds[originalIndex];
              if (originalId !== mediaId) {
                result[originalId] = mediaItem;
              }
            }
          } else {
            console.warn(`[batchGetMediaItems] No media item found for ID: ${mediaId}`);
          }
        } catch (individualError) {
          console.error(
            `[batchGetMediaItems] Error fetching individual media ID ${mediaId}:`,
            individualError,
          );
          // Continue with other IDs even if one fails
        }
      }

      // Fallback: Try to fetch any missing IDs individually
      const missingIds = uncachedIds.filter((id) => !result[id]);
      if (missingIds.length > 0) {
        for (const mediaId of missingIds) {
          try {
            const item = await this.getMediaItemById(mediaId);
            if (item) {
              result[mediaId] = item;

              // Also store under the original ID if it was different
              const originalIndex = processedIds.indexOf(mediaId);
              if (originalIndex !== -1) {
                const originalId = validMediaIds[originalIndex];
                if (originalId !== mediaId) {
                  result[originalId] = item;
                }
              }
            }
          } catch (error) {
            console.error(`Failed to fetch item ${mediaId} in fallback:`, error);
          }
        }
      }

      return result;
    } catch (error) {
      console.error(`Error in batch fetching media items:`, error);
      return {};
    }
  }

  /**
   * Update media metadata using the AI Agents API and Core API
   * Updates both backend systems to ensure consistency
   *
   * @param mediaId Media ID to update
   * @param metadata Object containing metadata to update
   * @returns Promise with the updated media object
   */
  async updateMediaMetadata(
    mediaId: string,
    metadata: {
      description?: string;
      copyright?: string;
      author?: string;
      location?: string;
      tags?: string[];
      metadata?: {
        tags?: string[];
        author?: string;
        location?: string;
        copyright?: string;
      };
      customProperties?: Record<string, any>;
    },
  ): Promise<MediaObject | null> {
    try {
      // Ensure we have agent ID before making the request
      const agent_id = await this.getAgentId();

      // Handle the case where metadata is sent in a flat structure or nested structure
      let tags = metadata.tags || [];
      let author = metadata.author || '';
      let location = metadata.location || '';
      let copyright = metadata.copyright || '';
      let description = metadata.description || '';

      // If metadata object had nested metadata, use those values directly
      if (metadata.metadata) {
        if (metadata.metadata.tags) tags = metadata.metadata.tags;
        if (metadata.metadata.author) author = metadata.metadata.author;
        if (metadata.metadata.location) location = metadata.metadata.location;
        if (metadata.metadata.copyright) copyright = metadata.metadata.copyright;
      }

      // The backend expects metadata record with proper types
      // Important: Do NOT stringify the tags array - the API expects an actual array
      const metadataRecord: Record<string, any> = {
        // Keep tags as an array, limited to 10 items as required by the API
        tags: Array.isArray(tags)
          ? tags.slice(0, 10)
          : typeof tags === 'string'
            ? [tags].slice(0, 10)
            : [],
        author: author || '',
        location: location || '',
        copyright: copyright || '',
      };

      // Add any custom properties
      if (metadata.customProperties) {
        Object.entries(metadata.customProperties).forEach(([key, value]) => {
          metadataRecord[key] = typeof value === 'string' ? value : JSON.stringify(value);
        });
      }

      const requestData = {
        description: description || '',
        metadata: metadataRecord,
        agent_id, // Include agent_id in the request
      };

      const response = await axios.patch(
        `${this.beApiUrl}/ai-agent/media/${mediaId}`,
        requestData,
        { headers: this.headers },
      );

      // Clear cache for this item
      const cacheKey = `mediaItem:${mediaId}`;
      RequestMediaService.requestFormCache.delete(cacheKey);

      // Check if we have response data to work with
      if (response.data && typeof response.data === 'object') {
        // Convert the response data into a properly formatted media object
        // that can be used directly in the UI
        const updatedMedia = {
          ...response.data,
          // Ensure metadata is properly structured for the UI components
          metadata: response.data.metadata || metadataRecord,
          // Make sure we include the updated values in custom_properties as well for UI consistency
          custom_properties: {
            ...(response.data.custom_properties || {}),
            description: description || response.data.description || '',
            author: author || response.data.metadata?.author || '',
            location: location || response.data.metadata?.location || '',
            copyright: copyright || response.data.metadata?.copyright || '',
          },
        };

        return updatedMedia as MediaObject;
      }

      return response.data;
    } catch (error) {
      console.error(`Error updating media metadata for ID ${mediaId}:`, error);
      // Add more detailed error information for debugging
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Helper method to convert FormMediaObject to MediaObject
   * This is used to convert uploaded files to the format expected by the MediaGallery component
   */
  convertFormMediaToMediaObject(formMedia: FormMediaObject): MediaObject {
    // Generate a unique ID to identify this media object
    const uniqueId = `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    return {
      id: formMedia.id || uniqueId,
      file_name: formMedia.name,
      mime_type: formMedia.type,
      size: formMedia.size || 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      index_url: formMedia.preview || '',
      url: formMedia.url || '',
      collection_name: 'media_library', // Required by MediaObject interface
      custom_properties: {}, // Required by MediaObject interface
      tags: formMedia.tags || [],
      generated_conversions: {}, // Required by MediaObject interface
      generated_resolutions: {}, // Required by MediaObject interface
      comment: undefined, // Required by MediaObject interface
      metadata: {
        description: formMedia.description || '',
        author: formMedia.author || '',
        location: formMedia.location || '',
        copyright: formMedia.copyright || '',
      },
      representations: [], // Required by MediaObject interface
    };
  }

  /**
   * Register an upload in the global registry
   * @param uploadId ID of the upload to register
   * @param upload Upload instance to register
   */
  registerUpload(uploadId: string, upload: tus.Upload): void {
    if (!uploadId || !upload) {
      console.error('Cannot register upload: Invalid upload ID or upload instance');
      return;
    }

    RequestMediaService.activeUploads.set(uploadId, upload);
  }

  /**
   * Cancel an upload by its ID
   * @param uploadId ID of the upload to cancel
   * @returns Boolean indicating whether cancellation was successful
   */
  cancelUpload(uploadId: string): boolean {
    const upload = RequestMediaService.activeUploads.get(uploadId);
    if (!upload) {
      console.error(`No active upload found with ID: ${uploadId}`);
      return false;
    }

    try {
      // Abort the upload
      upload.abort();

      // Remove from active uploads
      RequestMediaService.activeUploads.delete(uploadId);
      return true;
    } catch (error) {
      console.error(`Error cancelling upload with ID: ${uploadId}:`, error);
      return false;
    }
  }

  /**
   * Upload a media file and get back the MediaObject
   * @param file File to upload
   * @param directoryId Optional directory ID to upload to
   * @param progressCallback Optional callback for upload progress
   * @param uploadInstanceCallback Optional callback to get the upload instance for cancellation
   * @param uploadId Optional ID to use for this upload (to track it for cancellation)
   * @param userPath Optional user path array for directory-aware uploads (alternative to directoryId)
   * @returns Promise with the MediaObject
   */
  async uploadMedia(
    file: File,
    directoryId?: string | null,
    progressCallback?: (progress: number) => void,
    uploadInstanceCallback?: (upload: tus.Upload) => void,
    uploadId?: string,
    userPath?: string[],
    brandId?: string, // Brand ID to pass as user_id parameter
  ): Promise<MediaObject> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();
      if (!agent_id) {
        throw new Error('Agent ID is required but was not found');
      }

      let upload_url: string;
      let upload_token: string;
      let responseId: string;

      try {
        // Set up the request payload
        const requestPayload: any = {
          agent_id,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
        };

        // Add brand ID as user_id if provided
        if (brandId) {
          requestPayload.user_id = brandId;
          console.debug(
            '🔧 RequestMediaService.uploadMedia: Adding user_id to get-upload-url payload:',
            brandId,
          );
        }

        // Add directory ID if provided
        if (directoryId) {
          requestPayload.directory_id = directoryId;
        }

        // Add user path if provided
        if (userPath) {
          requestPayload.user_path = userPath;
        }

        // Make the API call with a timeout to prevent hanging
        const uploadUrlResponse = await axios.post(
          `${this.beApiUrl}/ai-agent/media/get-upload-url`,
          requestPayload,
          {
            headers: this.headers,
            timeout: 15000, // 15 second timeout to prevent indefinite hanging
          },
        );

        let responseData = uploadUrlResponse.data;

        // Extract the upload URL and token from the response
        if (responseData.upload_url && responseData.upload_token) {
          upload_url = responseData.upload_url;
          upload_token = responseData.upload_token;
          responseId = responseData.id || '';
        } else if (
          responseData.data &&
          responseData.data.upload_url &&
          responseData.data.upload_token
        ) {
          upload_url = responseData.data.upload_url;
          upload_token = responseData.data.upload_token;
          responseId = responseData.data.id || '';
        } else {
          console.error(
            '❌ Missing upload_url or upload_token in response:',
            JSON.stringify(responseData, null, 2),
          );
          throw new Error('Failed to get upload URL or token from server response');
        }
      } catch (error: any) {
        console.error('Error getting upload URL:', error.message);
        if (error.response) {
          console.error('Error response details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
          });
        }
        throw new Error(`Failed to generate upload URL: ${error.message}`);
      }

      // Use TUS for resumable uploads - following the working implementation pattern
      return new Promise((resolve, reject) => {
        if (progressCallback) {
          progressCallback(5); // Signal that we're starting the upload process
        }

        // Configure the tus upload with correct parameters
        const upload = new tus.Upload(file, {
          uploadUrl: upload_url,
          headers: {
            'x-signature': upload_token,
            Authorization: this.headers.Authorization,
          },
          onError: (error) => {
            console.error('Upload failed:', error);
            reject(error);
          },
          // Progress handler - report progress percentage
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = Math.round((bytesUploaded / bytesTotal) * 90); // Scale to 90% to leave room for API confirmation
            if (progressCallback) {
              progressCallback(percentage);
            }
          },
          // Success handler
          onSuccess: async () => {
            try {
              if (progressCallback) {
                progressCallback(92); // Signal that upload is complete, now finding the media item
              }

              // Wait longer for the server to process the upload (4 seconds to ensure indexing)
              await new Promise((r) => setTimeout(r, 4000));

              // Instead of confirmation, we'll find the file in the media library
              // Multiple attempts to find the file with increasing delay
              let mediaObject: MediaObject | null = null;
              let attempts = 0;
              const maxAttempts = 3;

              while (!mediaObject && attempts < maxAttempts) {
                attempts++;

                // Fetch recent media items
                const response = await this.getMediaItems(undefined, true);

                // Find recently created files (within the last 60 seconds)
                const recentFiles = response.files.filter((f) => {
                  const createdTime = new Date(f.created_at).getTime();
                  const currentTime = new Date().getTime();
                  return currentTime - createdTime < 60000; // 60 seconds
                });

                // Sort by most recently created
                recentFiles.sort(
                  (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                );

                // First try to find an exact filename match among recent files
                mediaObject = recentFiles.find((f) => f.file_name === file.name) || null;

                // If not found by exact name, try matching by name and similar size (±10%)
                if (!mediaObject) {
                  const fileSizeToFind = file.size;
                  mediaObject =
                    recentFiles.find((f) => {
                      // Use name similarity
                      const nameSimilar = f.file_name
                        ?.toLowerCase()
                        .includes(file.name.toLowerCase());

                      // Check if size is within 10% of expected size
                      const sizeMatch =
                        f.size && fileSizeToFind
                          ? f.size > fileSizeToFind * 0.9 && f.size < fileSizeToFind * 1.1
                          : false;

                      return (nameSimilar && sizeMatch) || f.file_name === file.name;
                    }) || null;
                }

                // If still not found, try to find by MIME type and size
                if (!mediaObject) {
                  const mimeType = file.type;
                  const fileSizeToFind = file.size;

                  mediaObject =
                    recentFiles.find((f) => {
                      // Check MIME type
                      const mimeMatch = f.mime_type === mimeType;

                      // Check if size is within 10% of expected size
                      const sizeMatch =
                        f.size && fileSizeToFind
                          ? f.size > fileSizeToFind * 0.9 && f.size < fileSizeToFind * 1.1
                          : false;

                      return mimeMatch && sizeMatch;
                    }) || null;
                }

                // If still not found, just take the most recent file
                if (!mediaObject && recentFiles.length > 0) {
                  mediaObject = recentFiles[0];
                }

                if (mediaObject) {
                  break;
                }

                // If not found, wait before trying again
                if (!mediaObject && attempts < maxAttempts) {
                  await new Promise((r) => setTimeout(r, 1000 * attempts)); // Increasing wait time
                }
              }

              if (!mediaObject) {
                // Instead of failing, create a mock media object using the file information
                // This is a fallback when we can't find the file but know the upload succeeded
                console.warn(
                  'Could not find the uploaded media item after multiple attempts. Creating a mock response.',
                );

                // Create a temporary object with the file info we have and all required MediaObject properties
                mediaObject = {
                  id: `temp-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9]/g, '')}`,
                  name: file.name,
                  file_name: file.name,
                  type: file.type,
                  mime_type: file.type,
                  size: file.size,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  url: '', // We don't have the URL, but the upload succeeded
                  preview: '',
                  collection_name: '',
                  custom_properties: {},
                  representations: {},
                  generated_resolutions: [],
                  index_url: '',
                  directory_id: null,
                  tags: [],
                } as unknown as MediaObject; // Use double casting to avoid TypeScript errors

                // Log a warning but don't fail the upload
                console.warn('Created temporary media object as fallback:', mediaObject);
              }

              if (progressCallback) {
                progressCallback(95); // Almost done
              }

              // Update the cache to include this new media item
              const now = Date.now();
              const cacheKey = `mediaItem:${mediaObject.id}`;
              RequestMediaService.requestFormCache.set(cacheKey, {
                data: mediaObject,
                timestamp: now,
              });

              if (progressCallback) {
                progressCallback(100); // All done!
              }

              resolve(mediaObject);
            } catch (error: any) {
              console.error('Error in upload confirmation:', error);
              reject(new Error(`Upload confirmation failed: ${error.message}`));
            }
          },
        });

        // If upload instance callback is provided, call it with the upload instance
        if (uploadInstanceCallback) {
          uploadInstanceCallback(upload);
        }

        // If an uploadId is provided, register the upload in the global registry
        if (uploadId) {
          this.registerUpload(uploadId, upload);
        }

        // Start the upload
        upload.start();
      });
    } catch (error: any) {
      console.error('Error in uploadMedia:', error);
      throw error;
    }
  }
}

// Export an instance for easy importing
export const requestMediaService = new RequestMediaService();
export default requestMediaService;

// Extend the RequestMediaService prototype with upload registration and cancellation methods
RequestMediaService.prototype.registerUpload = function (
  uploadId: string,
  upload: tus.Upload,
): void {
  if (!uploadId || !upload) {
    console.error('Cannot register upload: Invalid upload ID or upload instance');
    return;
  }

  RequestMediaService.activeUploads.set(uploadId, upload);
};

RequestMediaService.prototype.cancelUpload = function (uploadId: string): boolean {
  const upload = RequestMediaService.activeUploads.get(uploadId);
  if (!upload) {
    console.error(`No active upload found with ID: ${uploadId}`);
    return false;
  }

  try {
    // Abort the upload
    upload.abort();

    // Remove from active uploads
    RequestMediaService.activeUploads.delete(uploadId);
    return true;
  } catch (error) {
    console.error(`Error cancelling upload with ID: ${uploadId}:`, error);
    return false;
  }
};
